// =============================================================================
// io/storage/compress.ts — shrink a screenshot before it goes to Storage.
// -----------------------------------------------------------------------------
// Relocated from the old client's `compressScreenshotBlob`.
//
// ⚠ Lives in `io/` and not `domain/` because it needs `createImageBitmap`, a
// `<canvas>` and `toBlob` — browser APIs. Nothing isomorphic may depend on it.
//
// ── WHY COMPRESS AT ALL ──
//
// A full-screen chart capture is routinely 2–4 MB as PNG and under 300 KB as
// WebP at quality 0.85. These buckets have NO BACKUP and count against storage
// billing, and a trader attaching two screenshots per trade across a year is the
// difference between a few hundred megabytes and several gigabytes.
//
// ── 🚨 EVERY FAILURE PATH RETURNS THE ORIGINAL ──
//
// A screenshot that fails to compress must still be attachable. `createImageBitmap`
// throws on a malformed image, `toBlob` can hand back null, and a browser without
// WebP falls through to JPEG. Each of those returns the input rather than null,
// because the alternative is an attachment silently not happening.
// =============================================================================

/** Below this, compressing costs more than it saves. */
export const COMPRESS_MIN_BYTES = 200 * 1024;
export const COMPRESS_QUALITY = 0.85;
export const COMPRESSIBLE = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/bmp']);

export async function compressScreenshot(blob: Blob): Promise<Blob> {
  try {
    if (!blob || !COMPRESSIBLE.has(blob.type) || blob.size < COMPRESS_MIN_BYTES) return blob;

    const bmp = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;

    // ⚠ WHITE-FILL FIRST. JPEG has no alpha channel, so a transparent PNG —
    // which is what a chart capture with a cropped background often is —
    // composites onto BLACK in the JPEG fallback. The result is a screenshot
    // nobody can read, saved successfully.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bmp, 0, 0);
    bmp.close?.();

    const encode = (type: string): Promise<Blob | null> =>
      new Promise((res) => canvas.toBlob(res, type, COMPRESS_QUALITY));

    let out = await encode('image/webp');
    // ⚠ The TYPE is checked, not just the null. A browser without WebP encoding
    // silently returns a PNG from `toBlob('image/webp')` — bigger than the input,
    // which the size check below then rejects, but only because it is checked.
    if (!out || out.type !== 'image/webp') out = await encode('image/jpeg');

    // ⚠ Only if it actually got SMALLER. Re-encoding an already-efficient JPEG
    // can grow it, and shipping a larger file while calling it compression is
    // worse than doing nothing.
    return out && out.size < blob.size ? out : blob;
  } catch {
    // See the header: an attachment that fails to compress must still attach.
    return blob;
  }
}
